
import type { PropType } from 'vue'
import type { AuditItem } from '../../../../models'
import type { AuditItemDetailsRow } from './audit-item-details-body.component.vue'
import { DomainObjectUtils } from '@empathyco/playboard-core-domain'
import { deepEqual } from '@empathyco/x-utils'
import { computed, defineComponent } from 'vue'
import { capitalizeSentence } from '../../../../filters'
import { EpbVirtualScroller } from '../../../shared/epb-virtual-scroller'
import AuditItemDetailsBody from './audit-item-details-body.component.vue'

export interface AuditItemDetailsMessages {
  previousState: string
  nextState: string
  fields?: Record<string, string>
}

export type AuditItemDetailsFieldType = 'array' | 'object' | 'other'

export default defineComponent({
  name: 'AuditItemDetails',
  components: {
    EpbVirtualScroller,
    AuditItemDetailsBody,
  },
  props: {
    item: {
      type: Object as PropType<AuditItem>,
      required: true,
    },
    messages: {
      type: Object as PropType<AuditItemDetailsMessages>,
      required: true,
    },
    ignoredRows: {
      type: Array as PropType<Array<string>>,
      default: () => [],
    },
    useVirtualScroller: Boolean,
  },
  setup(props) {
    const defaultEmptyValue = props.item.payload.previousState ? ' ' : undefined

    function isArray(previousState: unknown, nextState: unknown): boolean {
      return Array.isArray(previousState) || Array.isArray(nextState)
    }

    function isObject(previousState: unknown, nextState: unknown): boolean {
      return DomainObjectUtils.isObject(previousState) || DomainObjectUtils.isObject(nextState)
    }

    function getFieldType(previousState: unknown, nextState: unknown): AuditItemDetailsFieldType {
      let type: AuditItemDetailsFieldType
      if (isArray(previousState, nextState)) {
        type = 'array'
      } else if (isObject(previousState, nextState)) {
        type = 'object'
      } else {
        type = 'other'
      }
      return type
    }

    function getRowState(state: unknown, type: AuditItemDetailsFieldType): unknown {
      return type === 'array'
        ? (state as Array<unknown>)?.length.toString()
        : type === 'object'
          ? undefined
          : state
    }

    function areStatesEqual(previousState: unknown, nextState: unknown): boolean {
      if (!props.item.payload.previousState || !props.item.payload.nextState) {
        return true
      }
      if (previousState instanceof Date && nextState instanceof Date) {
        return previousState.getTime() === nextState.getTime()
      }
      return deepEqual(previousState, nextState)
    }

    function getFieldLabel(fieldId: string): string {
      const fieldName = fieldId.split('.').pop()

      return (
        props.messages.fields?.[fieldId] ??
        props.messages.fields?.[fieldName] ??
        capitalizeSentence(fieldName)
      )
    }

    function getObjectRow({
      fieldId,
      previousState,
      nextState,
      level,
      key,
    }: {
      fieldId: string
      previousState: unknown
      nextState: unknown
      level: number
      key: string
    }): AuditItemDetailsRow {
      const type = getFieldType(previousState, nextState)
      const rowPreviousState = getRowState(previousState, type)
      const rowNextState = getRowState(nextState, type)
      const stateChanged = type !== 'object' && !areStatesEqual(rowPreviousState, rowNextState)
      return {
        id: fieldId,
        label: getFieldLabel(fieldId),
        previousState: rowPreviousState,
        nextState: rowNextState,
        stateChanged,
        level,
        key,
      }
    }

    function getArrayRow({
      fieldId,
      index,
      previousState,
      nextState,
      level,
      key,
    }: {
      fieldId: string
      index: number
      previousState: unknown
      nextState: unknown
      level: number
      key: string
    }): AuditItemDetailsRow {
      const type = getFieldType(previousState, nextState)
      const rowPreviousState = getRowState(previousState, type) ?? defaultEmptyValue
      const rowNextState = getRowState(nextState, type) ?? defaultEmptyValue
      const stateChanged = type !== 'object' && !areStatesEqual(rowPreviousState, rowNextState)

      return {
        id: fieldId,
        label: index.toString(),
        previousState: rowPreviousState,
        nextState: rowNextState,
        stateChanged,
        level,
        key,
      }
    }

    function getArrayRows({
      fieldId,
      previousState,
      nextState,
      level,
      parentKey,
    }: {
      fieldId: string
      previousState: Array<unknown> | undefined
      nextState: Array<unknown> | undefined
      level: number
      parentKey: string
    }): Array<AuditItemDetailsRow> {
      const result: Array<AuditItemDetailsRow> = []
      const maxLength = Math.max(previousState?.length ?? 0, nextState?.length ?? 0)
      for (let index = 0; index < maxLength; index++) {
        const itemPreviousState = previousState?.[index]
        const itemNextState = nextState?.[index]
        const key = `${parentKey}[${index}]`
        result.push(
          getArrayRow({
            fieldId,
            index,
            previousState: itemPreviousState,
            nextState: itemNextState,
            level,
            key,
          }),
          ...getChildRows({
            fieldId,
            previousState: itemPreviousState,
            nextState: itemNextState,
            level,
            key,
          }),
        )
      }
      return result
    }

    function getChildRows({
      fieldId,
      previousState,
      nextState,
      level,
      key,
      parentId,
      parentKey,
    }: {
      fieldId: string
      previousState: unknown
      nextState: unknown
      level: number
      key: string
      parentId?: string
      parentKey?: string
    }): Array<AuditItemDetailsRow> {
      let result: Array<AuditItemDetailsRow> = []
      const fieldType = getFieldType(previousState, nextState)
      if (fieldType === 'other') {
        result = []
      } else {
        const rowIsIgnored = props.ignoredRows.includes(fieldId)
        let childFieldId: string, childLevel: number, childParentId: string, childParentKey: string
        if (!rowIsIgnored) {
          childFieldId = fieldId
          childLevel = level + 1
          childParentId = fieldId
          childParentKey = key
        } else {
          childFieldId = parentId
          childLevel = level
          childParentId = parentId
          childParentKey = parentKey
        }
        if (fieldType === 'array') {
          result.push(
            ...getArrayRows({
              fieldId: childFieldId,
              previousState: previousState as Array<unknown>,
              nextState: nextState as Array<unknown>,
              level: childLevel,
              parentKey: childParentKey,
            }),
          )
        } else if (fieldType === 'object') {
          result.push(
            ...getObjectRows({
              parentId: childParentId,
              previousState: previousState as Record<string, unknown>,
              nextState: nextState as Record<string, unknown>,
              level: childLevel,
              parentKey: childParentKey,
            }),
          )
        }
      }
      return result
    }

    function getObjectRows({
      parentId,
      previousState,
      nextState,
      level = 1,
      parentKey,
    }: {
      parentId?: string
      previousState: Record<string, unknown> | undefined
      nextState: Record<string, unknown> | undefined
      level?: number
      parentKey?: string
    }): Array<AuditItemDetailsRow> {
      return Object.keys({ ...previousState, ...nextState }).reduce<Array<AuditItemDetailsRow>>(
        (result, key) => {
          const fieldId = parentId ? `${parentId}.${key}` : key
          const fieldPreviousState = previousState?.[key] ?? defaultEmptyValue
          const fieldNextState = nextState?.[key] ?? defaultEmptyValue
          const fieldKey = parentKey ? `${parentKey}.${key}` : key
          const rowIsIgnored = props.ignoredRows.includes(fieldId)
          if (!rowIsIgnored) {
            result.push(
              getObjectRow({
                fieldId,
                previousState: fieldPreviousState,
                nextState: fieldNextState,
                level,
                key: fieldKey,
              }),
            )
          }
          result.push(
            ...getChildRows({
              fieldId,
              previousState: fieldPreviousState,
              nextState: fieldNextState,
              level,
              key: fieldKey,
              parentId,
              parentKey,
            }),
          )
          return result
        },
        [],
      )
    }

    const rows = computed<Array<AuditItemDetailsRow>>(() => {
      const { previousState, nextState } = props.item.payload
      return getObjectRows({ previousState, nextState })
    })

    return {
      baseClass: 'epb-audit-item-details',
      rows,
    }
  },
})
